import React from 'react';
import PropTypes from 'prop-types';
import TimeAgo from 'react-timeago';
import classnames from 'classnames';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import ProviderShiftContextCodes from './enums/provider-shift-context-codes';
import './styles.scss';

const setupConsultAlertSettings = (providers, consultSpecialtyCd) => {
  return providers.reduce((settings, { id, phone_numbers, consult_alert_settings }) => {
    const notification = consult_alert_settings.find(({ specialty_cd }) => specialty_cd === consultSpecialtyCd);
    const commMethod = notification && notification.comm_methods.find(({ comm_method_cd }) => comm_method_cd === 'SMS');

    if (notification && commMethod) {
      settings[id] = {
        isEnabled: commMethod.enabled,
        phoneNumber: commMethod.enabled && commMethod.party_id ? phone_numbers.find(({ id }) => id === commMethod.party_id) : null
      };
    }

    return settings;
  }, {});
};

class ProviderCoverageTable extends React.Component {
  constructor(props) {
    super(props);

    const consultAlertSettings = setupConsultAlertSettings(
      flatten(Object.values(props.providers)),
      props.specialtyCd
    );
    this.state = {
      clockedIn: false,
      inScheduledShift: false,
      inPreferredProviderShift: false,
      availY: false,
      availN: false,
      availU: false,
      availO: false,
      cqsStatusA: false,
      cqsStatusR: false,
      cqsStatusB: false,
      cqsStatusU: false,
      cqsOnShift: false,
      consultAlertSettings,
      showOptedInFilter: !!Object.keys(consultAlertSettings).length,
      isOptedIn: false
    };

    this.renderProvider = this.renderProvider.bind(this);
    this.renderNotificationAction = this.renderNotificationAction.bind(this);
    this.isProviderOptedIn = this.isProviderOptedIn.bind(this);
  }

  showProvider = p => {
    const cqs_status = get(p, 'cqs_attributes.status', null);
    const cqs_on_shift = get(p, 'cqs_attributes.on_shift', null);
    return (this.isInScheduledShift(p) && this.state.inScheduledShift) ||
      (this.isInPreferredProviderShift(p) && this.state.inPreferredProviderShift) ||
      (p.clocked_in && this.state.clockedIn) ||
      (cqs_on_shift && this.state.cqsOnShift) ||
      (p.specified_availability === 'Y' && this.state.availY) ||
      (p.specified_availability === 'N' && this.state.availN) ||
      (p.specified_availability === 'O' && this.state.availO) ||
      (p.specified_availability === 'U' && this.state.availU) ||
      (cqs_status === 'AVAILABLE' && this.state.cqsStatusA) ||
      (cqs_status === 'RESERVED' && this.state.cqsStatusR) ||
      (cqs_status === 'BUSY' && this.state.cqsStatusB) ||
      (cqs_status === 'UNAVAILABLE' && this.state.cqsStatusU) ||
      (this.state.isOptedIn && this.isProviderOptedIn(p)) ||
      !(
        this.state.inScheduledShift ||
        this.state.inPreferredProviderShift ||
        this.state.clockedIn ||
        this.state.cqsOnShift ||
        this.state.availY ||
        this.state.availN ||
        this.state.availO ||
        this.state.availU ||
        this.state.cqsStatusA ||
        this.state.cqsStatusR ||
        this.state.cqsStatusB ||
        this.state.cqsStatusU ||
        this.state.isOptedIn
      );
  };

  inShiftIndicator = inShift => {
    if (inShift) {
      return <b style={{ color: 'purple' }}>P</b>;
    }
  };

  availabilityIndicator = specifiedAvailability => {
    switch (specifiedAvailability) {
      case 'Y':
        return <b style={{ color: 'green' }}>Y</b>;
      case 'N':
        return <b style={{ color: 'red' }}>N</b>;
      case 'O':
        return <b style={{ color: 'red' }}>O</b>;
      case 'U':
        return <b>U</b>;
      default:
        return null;
    }
  };

  yesNo = question => (question ? 'Yes' : 'No');

  preferredFlag = flagIn => {
    if (flagIn.toLowerCase() === 'y') {
      return 'P';
    }
  };

  providerSpecialtiesTooltip = specialties => {
    if (specialties.length > 0) {
      return (
        <div className={'info_tooltip'} data-display-text={specialties.join(', ')}>
          {specialties[0]}
        </div>
      );
    }
  };

  lastActiveDate = d => {
    if (d && d !== null) {
      return <TimeAgo date={d} />;
    }
  };

  isInScheduledShift = provider =>
    [ProviderShiftContextCodes.SCHEDULED, ProviderShiftContextCodes.SCHEDULED_SHIFT].includes(
      get(provider, 'current_shift_code')
    );

  isInPreferredProviderShift = provider =>
    get(provider, 'current_shift_code') === ProviderShiftContextCodes.PREFERRED_PROVIDER;

  highlightedOnShift = (provider) =>
    this.props.showCqsOnShift && !provider.clocked_in && get(provider, 'cqs_attributes.on_shift')

  isProviderOptedIn = ({ id }) => {
    const consultAlertSetting = this.state.consultAlertSettings[id];
    return consultAlertSetting && consultAlertSetting.isEnabled;
  }

  renderNotificationAction = ({ id }) => {
    const { consultAlertSettings } = this.state;
    const { specialtyCd } = this.props;
    const consultAlertSetting = consultAlertSettings[id];
    const selectedPhoneNumberId = get(consultAlertSetting, 'phoneNumber.id');

    if (!consultAlertSetting) {
      return (
        <a
          href={`/providers/${id}/send_text_notification_form/`}
          className="send_text_notification_form_link"
        >
          Select Text
        </a>
      );
    }

    return (
      consultAlertSetting.isEnabled ? (
        <a
          href={`/providers/${id}/send_text_notification_form/?specialty_cd=${specialtyCd}&party_phone_fax_id=${selectedPhoneNumberId}`}
          className="send_text_notification_form_link"
        >
          Select Text
        </a>
      ) : (
        'Not Opted-IN'
      )
    );
  }

  renderProvider = p => {
    if (this.showProvider(p)) {
      const { consultAlertSettings } = this.state;
      const selectedPhoneNumberId = get(consultAlertSettings, `${p.id}.phoneNumber.id`);

      return (
        <tr
          key={p.id}
          id={`provider-${p.id}`}
          className={classnames('provider-row', { 'highlighted-provider': !p.clocked_in })}
        >
          <td>
            <a href={`/providers/${p.id}`}>View</a>
          </td>
          <td>
            {this.inShiftIndicator(p.in_shift)}
            {this.availabilityIndicator(p.specified_availability)}
          </td>
          <td>{p.first_nm}</td>
          <td>{p.last_nm}</td>
          <td>
            Assist: {this.yesNo(p.assist_alerts)} Urgent: {this.yesNo(p.urgent_alerts)}
          </td>
          <td>
            {p.phone_numbers.map(({ id, phone_fax_type, number, preferred_flag }, index2) => {
              const phoneNumber = `${phone_fax_type}: ${number} ${this.preferredFlag(preferred_flag) || ''}`.trim();
              return (
                <div key={index2}>
                  {id === selectedPhoneNumberId ? (<b>{phoneNumber}*</b>) : phoneNumber}
                </div>
              );
            })}
          </td>
          <td>{this.yesNo(p.clocked_in)}</td>

          {this.props.showCqsOnShift && (
            <td>
              <p className={classnames('on-shift', { 'highlighted-on-shift': this.highlightedOnShift(p) })}>
                {get(p, 'cqs_attributes.on_shift') ? 'On Shift' : ''}
              </p>
            </td>
          )}

          {this.props.showCqsStatus && (
            <td>{get(p, 'cqs_attributes.status')}</td>
          )}

          <td>
            { this.renderNotificationAction(p) }
          </td>
          <td>{p.clocked_in && p.available_for_video ? 'Phone/Video' : 'Phone'}</td>
          <td>{p.role}</td>
          <td>{this.yesNo(p.allow_preselected_provider)}</td>
          <td>{this.providerSpecialtiesTooltip(p.provider_specialties)}</td>
          <td>
            <div
              className={'event_timeline_modal_trigger info_tooltip'}
              data-entity-id={p.id}
              data-display-text={'Loading...'}
              data-entity-type={'provider'}
              data-toggle={'tooltip'}
            >
              <img
                className={'timeline_modal_trigger_icon'}
                src={'/assets/icon_group.png'}
                alt={'File Icon'}
              />
            </div>
          </td>
          <td>{this.lastActiveDate(p.last_active_time)}</td>
          <td>{this.lastActiveDate(get(p, 'cqs_attributes.status_updated_at'))}</td>
        </tr>
      );
    }
  };

  render() {
    const { providers, showPreferredProvidersFilter, showCqsStatus, showCqsOnShift } = this.props;
    const { showOptedInFilter } = this.state;

    return (
      <div>
        <p>
          Clocked in?{' '}
          <input
            type={'checkbox'}
            onChange={e => {
              this.setState({ clockedIn: e.target.checked });
            }}
            name="clocked-in"
          />
          <br />
          In Shift?{' '}
          <input
            type={'checkbox'}
            onChange={e => {
              this.setState({ inScheduledShift: e.target.checked });
            }}
            name="in-shift"
          />
          <br />
          {showPreferredProvidersFilter && (
            <span>
              In Preferred Provider Shift?{' '}
              <input
                type={'checkbox'}
                onChange={e => {
                  this.setState({ inPreferredProviderShift: e.target.checked });
                }}
                name="in-preferred-provider-shift"
              />
              <br />
            </span>
          )}
          Available? Y{' '}
          <input
            type={'checkbox'}
            onChange={e => {
              this.setState({ availY: e.target.checked });
            }}
            name="available-y"
          />{' '}
          N{' '}
          <input
            type={'checkbox'}
            onChange={e => {
              this.setState({ availN: e.target.checked });
            }}
            name="available-n"
          />{' '}
          O{' '}
          <input
            type={'checkbox'}
            onChange={e => {
              this.setState({ availO: e.target.checked });
            }}
            name="available-o"
          />{' '}
          U{' '}
          <input
            type={'checkbox'}
            onChange={e => {
              this.setState({ availU: e.target.checked });
            }}
            name="available-u"
          />{' '}

          {showCqsOnShift && (
            <span>
              <br />
              Calendar:{' '}
              <input
                type={'checkbox'}
                onChange={e => {
                  this.setState({ cqsOnShift: e.target.checked });
                }}
                name="cqs-on-shift"
              />{' On Shift'}
            </span>
          )}

          {showCqsStatus && (
            <span>
              <br />
              CQS status Available{' '}
              <input
                type={'checkbox'}
                onChange={e => {
                  this.setState({ cqsStatusA: e.target.checked });
                }}
                name="cqs-status-a"
              />{' '}
              Reserved{' '}
              <input
                type={'checkbox'}
                onChange={e => {
                  this.setState({ cqsStatusR: e.target.checked });
                }}
                name="cqs-status-r"
              />{' '}
              Busy{' '}
              <input
                type={'checkbox'}
                onChange={e => {
                  this.setState({ cqsStatusB: e.target.checked });
                }}
                name="cqs-status-b"
              />{' '}
              Unavailable{' '}
              <input
                type={'checkbox'}
                onChange={e => {
                  this.setState({ cqsStatusU: e.target.checked });
                }}
                name="cqs-status-u"
              />{' '}
            </span>
          )}
          {showOptedInFilter && (
            <span>
              <br />
              Is Opted-In?{' '}
              <input
                type={'checkbox'}
                onChange={e => {
                  this.setState({ isOptedIn: e.target.checked });
                }}
                name="is-opted-in"
              />
            </span>
          )}
          <br />
          <br />
        </p>
        {Object.keys(providers).map((startTime, index) => (
          <div key={index}>
            <div style={{ backgroundColor: '#c0c0c0' }}>Start Time: {startTime}</div>
            <table id="provider-coverage-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Available?</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Alerts</th>
                  <th>Contact</th>
                  <th>Clocked In?</th>
                  {showCqsOnShift && (
                    <th>Calendar</th>
                  )}
                  {showCqsStatus && (
                    <th>CQS status</th>
                  )}
                  <th>Send a Text</th>
                  <th>Method</th>
                  <th>Primary Role</th>
                  <th>Select?</th>
                  <th>Specialties</th>
                  <th>Timeline</th>
                  <th>Last Active Time</th>
                  <th>Last CQS Status Change</th>
                </tr>
              </thead>
              <tbody>{providers[startTime].map(this.renderProvider)}</tbody>
            </table>
          </div>
        ))}
      </div>
    );
  }
}

ProviderCoverageTable.propTypes = {
  providers: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        allow_preselected_provider: PropTypes.bool,
        id: PropTypes.number,
        first_nm: PropTypes.string,
        last_nm: PropTypes.string,
        assist_alerts: PropTypes.bool,
        urgent_alerts: PropTypes.bool,
        clocked_in: PropTypes.bool,
        available_for_video: PropTypes.bool,
        role: PropTypes.string,
        provider_specialties: PropTypes.arrayOf(PropTypes.string),
        available_via: PropTypes.string,
        last_active_time: PropTypes.string,
        phone_numbers: PropTypes.arrayOf(PropTypes.object),
        specified_availability: PropTypes.string,
        in_shift: PropTypes.bool,
        current_shift_code: PropTypes.string,
        cqs_attributes: PropTypes.shape({
          status: PropTypes.string,
          on_shift: PropTypes.bool,
          tpn_sequence: PropTypes.number,
          status_updated_at: PropTypes.string
        })
      })
    )
  ),
  showPreferredProvidersFilter: PropTypes.bool.isRequired,
  showCqsStatus: PropTypes.bool.isRequired,
  showCqsOnShift: PropTypes.bool.isRequired,
  specialtyCd: PropTypes.string.isRequired
};

export default ProviderCoverageTable;
